/**
 * How a live Light <-> Graphite switch looks (KAN-304). Without this, the page flipped in one frame while
 * every element with its own colour transition (buttons, fields, the nav's selected card) caught up 150-320ms
 * later, which read as a flicker. Now:
 *   - every element's own transitions are paused for the switch (`html.cvx-theme-switching`), so nothing lags;
 *   - where the browser has View Transitions (Safari 18+, Chrome 111+) the whole screen cross-fades once, 240ms;
 *   - Reduce Motion, a hidden tab, or an older browser get a clean instant switch.
 * The first paint and boot never animate: the caller only asks for motion on a LIVE change.
 */

import './themeSwitchMotion.css';

export const THEME_SWITCHING_CLASS = 'cvx-theme-switching';

// Bumps on every switch: only the newest one hands element transitions back (KAN-1142).
let switchSeq = 0;

// A second switch within the fade aborts the first; Safari then rejects its `ready` /
// `updateCallbackDone` and reports them as unhandled unless something listens (KAN-1142).
function quiet(p) {
  if (p && typeof p.catch === 'function') p.catch(() => {});
}

function prefersReducedMotion() {
  try { return !!window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
}

/**
 * Calls `apply` (which stamps the new theme / language) inside ONE whole-screen cross-fade. Returns a promise
 * that settles once it has finished. Shared by the theme switch and the language switch (KAN-304): `apply`
 * must commit its change synchronously (React callers wrap their setState in `flushSync`).
 */
export function runScreenCrossFade(apply, doc = typeof document !== 'undefined' ? document : null) {
  const root = doc?.documentElement;
  if (!root) { apply(); return Promise.resolve(); }
  root.classList.add(THEME_SWITCHING_CLASS);
  switchSeq += 1;
  const seq = switchSeq;
  const done = () => { if (seq === switchSeq) root.classList.remove(THEME_SWITCHING_CLASS); };
  const canFade = typeof doc.startViewTransition === 'function'
    && doc.visibilityState !== 'hidden'
    && !prefersReducedMotion();
  if (canFade) {
    try {
      const vt = doc.startViewTransition(() => { apply(); });
      quiet(vt?.ready);
      quiet(vt?.updateCallbackDone);
      return Promise.resolve(vt.finished).catch(() => {}).then(done);
    } catch { /* fall through to the instant switch */ }
  }
  apply();
  // One painted frame with transitions paused, then hand them back.
  return new Promise((resolve) => {
    const raf = typeof window !== 'undefined' && window.requestAnimationFrame
      ? window.requestAnimationFrame.bind(window)
      : (fn) => setTimeout(fn, 16);
    raf(() => raf(() => { done(); resolve(); }));
  });
}

/** The theme switch's original name; same function. */
export const runThemeSwitch = runScreenCrossFade;
